import React, { useState, useEffect, useCallback } from "react";
import { Button, Col, Row } from "react-bootstrap";
import Axios from "axios";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";

import { dirname } from "../../../util/resourcePath";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";

import LoaderGraph from "../../general/LoaderGraph";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";

function SummaryInvoice(props) {
	const user = useUser();

	const directory = dirname();

	const [isMakroInvoice, setIsMakroInvoice] = useState(false);
	const [isCFWInvoice, setIsCFWInvoice] = useState(false);
	const [isDoHomeInvoice, setIsDoHomeInvoice] = useState(false);

	const [hidden, setHidden] = useState(true);
	const [showGraph, setShowGraph] = useState("");
	const [phase1, setPhase1] = useState(null);
	const [phase2, setPhase2] = useState(null);
	const [phase1IsEnabled, setPhase1IsEnabled] = useState(false);
	const [phase2IsEnabled, setPhase2IsEnabled] = useState(false);

	const [phaseGYS6, setPhaseGYS6] = useState(null);
	const [phaseS4, setPhaseS4] = useState(null);
	const [phaseGYS6IsEnabled, setPhaseGYS6IsEnabled] = useState(false);
	const [phaseS4IsEnabled, setPhaseS4IsEnabled] = useState(false);

	const [doHomeEntityGroups, setDoHomeEntityGroups] = useState([]);
	const [doHomeEnabled, setDoHomeEnabled] = useState({});

	const [selectedMonth, setSelectedMonth] = useState(props.selectedMonth);

	// ---- Server-side PDF preview state ----
	const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewFileName, setPreviewFileName] = useState("");

	const btnStyle = {
		marginRight: "10%",
	};

	const roles = user.roles;
	const viewCurrMonth = roles.includes(
		"administration:InvoiceTable:_viewInvCurMonth"
	);
	const clientViewInv = roles.includes(
		"administration:InvoiceTable:_clientViewInv"
	);
	// Min date of datepicker
	// Client 	: 2021-04-01
	// GY			 	: 2020-11-01
	const minDate =
		roles != null && clientViewInv && (isMakroInvoice || isCFWInvoice || isDoHomeInvoice)
			? moment("2021-04-01", "YYYY-MM-DD").toDate()
			: moment("2020-11-01", "YYYY-MM-DD").toDate();

	// Max date of datepicker
	// Regular		: Previous month
	// With Role	: Current month
	const maxDate =
		roles != null && viewCurrMonth
			? moment().add(1, "month").startOf("month").toDate()
			: moment().startOf("month").toDate();

	/**
	 * Close preview of invoice
	 */
	const closePreview = () => {
		setShowGraph("fadeOut");
		setTimeout(() => {
			setHidden(true);
			// Clean up blob URL to free memory
			if (previewPdfUrl) {
				URL.revokeObjectURL(previewPdfUrl);
				setPreviewPdfUrl(null);
			}
			setPreviewLoading(false);
			setPreviewFileName("");
		}, 400);
	};

	/**
	 * Fetch summary invoice PDF from server and display in preview overlay
	 * @param {Array} sites - Array of site objects for this phase
	 * @param {string} templateType - "Makro" | "CFW" | "DoHome"
	 * @param {string} phaseCode - e.g. "", "2", "GYS6", "S4", or a DoHome entity_id
	 */
	const fetchSummaryPdfPreview = useCallback(async (sites, templateType, phaseCode) => {
		setPreviewLoading(true);
		setPreviewPdfUrl(null);

		try {
			const table_names = sites.map((s) => s.table_name);
			const dateStr = selectedMonth.format("YYYY-MM");

			const response = await GYAxios.post(
				"/invoice/pdf/summary_invoice",
				{
					table_names,
					dateStr,
					templateType,
					phaseCode,
					invoiceCopyType: "ORIGINAL",
				},
				{ responseType: "blob", timeout: 120000 }
			);

			const blob = new Blob([response.data], { type: "application/pdf" });
			const url = URL.createObjectURL(blob);
			setPreviewPdfUrl(url);

			const fileName = `SummaryInvoice_${templateType}_${phaseCode || "Phase1"}_${dateStr}.pdf`;
			setPreviewFileName(fileName);
		} catch (error) {
			console.error("[SummaryInvoice] Server-side PDF preview failed:", error);
			Noti.create({
				type: "e",
				msg: "Failed to generate summary invoice preview. Please try again.",
			});
			closePreview();
		} finally {
			setPreviewLoading(false);
		}
	}, [selectedMonth]);

	/**
	 * Download the currently previewed PDF
	 */
	const downloadPdf = useCallback(() => {
		if (!previewPdfUrl) return;
		const a = document.createElement("a");
		a.href = previewPdfUrl;
		a.download = previewFileName || "summary_invoice.pdf";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}, [previewPdfUrl, previewFileName]);

	useEffect(() => {
		if (props.summaryInvoiceSites != null) {
			const sites = props.summaryInvoiceSites;

			const temp_phase_1 = sites.filter(props.makroSitesFilterByPhaseFn('PHASE_1'));
			const temp_phase_2 = sites.filter(props.makroSitesFilterByPhaseFn('PHASE_2'));

			const temp_phase_gys6 = sites.filter(props.CFWSitesFilterByPhaseFn('PHASE_GYS6'));
			const temp_phase_s4 = sites.filter(props.CFWSitesFilterByPhaseFn('PHASE_S4'));

			if (temp_phase_1.length > 0) setPhase1(temp_phase_1);
			else setPhase1(null);
			if (temp_phase_2.length > 0) setPhase2(temp_phase_2);
			else setPhase2(null);

			if (temp_phase_gys6.length > 0) setPhaseGYS6(temp_phase_gys6);
			else setPhaseGYS6(null);
			if (temp_phase_s4.length > 0) setPhaseS4(temp_phase_s4);
			else setPhaseS4(null);

			const dohomeSites = sites.filter(site => site.client_id === 386);
			const groupedByEntity = {};
			dohomeSites.forEach((s) => {
				const entityId = s.entity_id || "TH";
				if (!groupedByEntity[entityId]) groupedByEntity[entityId] = [];
				groupedByEntity[entityId].push(s);
			});
			const entityGroups = Object.entries(groupedByEntity)
				.map(([entityId, groupSites]) => ({ entityId, sites: groupSites }))
				.sort((a, b) => a.entityId.localeCompare(b.entityId));
			setDoHomeEntityGroups(entityGroups);
		}

		return () => {
			setPhase1(null);
			setPhase2(null);
			setPhaseGYS6(null);
			setPhaseS4(null);
			setDoHomeEntityGroups([]);
		};
	}, [props.summaryInvoiceSites]);

	useEffect(() => {
		setSelectedMonth(props.selectedMonth);
	}, [props.selectedMonth]);

	useEffect(() => {
		const endOfMonth = selectedMonth.endOf("month").format("YYYY-MM-DD");
		const param = {
			date: endOfMonth,
			viewStatus: props.viewLevel,
		};

		if (isMakroInvoice) {
			const MK1SitesReq = GYAxios.post("/reports/summary_groups", {
				...param,
				group: "MK1",
			});
			const MK2SitesReq = GYAxios.post("/reports/summary_groups", {
				...param,
				group: "MK2",
			});

			Axios.all([MK1SitesReq, MK2SitesReq]).then(
				Axios.spread((...responses) => {
					const MK1IsApproved = responses[0].data.isApproved;
					const MK2IsApproved = responses[1].data.isApproved;
					setPhase1IsEnabled(MK1IsApproved);
					setPhase2IsEnabled(MK2IsApproved);
				})
			);
		}

		if (isCFWInvoice) {
			const GYS6SitesReq = { url: "/reports/summary_groups_cfw", data: { ...param, group: "GYS6" } };
			const S4SitesReq = { url: "/reports/summary_groups_cfw", data: { ...param, group: "S4" } };


			Axios.all([GYAxios.post(GYS6SitesReq.url, GYS6SitesReq.data), GYAxios.post(S4SitesReq.url, S4SitesReq.data)]).then(
				Axios.spread((...responses) => {
					const GYS6IsApproved = responses[0].data.isApproved;
					const S4IsApproved = responses[1].data.isApproved;
					setPhaseGYS6IsEnabled(GYS6IsApproved); // MSA and COD
					setPhaseS4IsEnabled(S4IsApproved); // only COD (so far)
				})
			);
		}

		if (isDoHomeInvoice && doHomeEntityGroups.length > 0) {
			Promise.all(
				doHomeEntityGroups.map(({ entityId }) =>
					GYAxios.post("/reports/summary_groups_dohome", { ...param, group: entityId })
						.then((res) => ({ entityId, isApproved: !!res.data.isApproved }))
						.catch(() => ({ entityId, isApproved: false }))
				)
			).then((results) => {
				const enabledMap = {};
				results.forEach(({ entityId, isApproved }) => {
					enabledMap[entityId] = isApproved;
				});
				setDoHomeEnabled(enabledMap);
			});
		}
	}, [selectedMonth, isMakroInvoice, isCFWInvoice, isDoHomeInvoice, doHomeEntityGroups]);

	useEffect(() => {
		if (props.whichUser === "GY") {
			setIsMakroInvoice(true);
			setIsCFWInvoice(true);
			setIsDoHomeInvoice(true);
		} else if (props.whichUser === "CFW") {
			setIsMakroInvoice(false);
			setIsCFWInvoice(true);
			setIsDoHomeInvoice(false);
		} else if (props.whichUser === "Makro") {
			setIsMakroInvoice(true);
			setIsCFWInvoice(false);
			setIsDoHomeInvoice(false);
		} else if (props.whichUser === "DoHome") {
			setIsMakroInvoice(false);
			setIsCFWInvoice(false);
			setIsDoHomeInvoice(true);
		}
	}, [props.whichUser]);

	return props.ready ? (
		<>
			<div className="perf-table panel panel-default">
				<div className="panel-heading ">
					<Row>
						<Col md="6">
							<h2 className="panel-title" id="date">
								Summary Invoice
							</h2>
							<br />
							<div className="month-selector">
								Choose another Month: &nbsp;&nbsp;
								<DatePicker
									minDate={localTimeToPickerTime(minDate)}
									maxDate={localTimeToPickerTime(maxDate)}
									selected={localTimeToPickerTime(selectedMonth.toDate())}
									onChange={(date) => {
										setSelectedMonth(moment(pickerTimeTolocalTime(date)));
										props.setSelectedMonth(moment(pickerTimeTolocalTime(date)));
										props.setReady(false);
									}}
									dateFormat="MMM yyyy"
									showMonthYearPicker
								/>
							</div>
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							<img
								src={`${directory}/images/reload.png`}
								onClick={() => {
									props.setReady(false);
								}}
								style={{
									height: "35px",
									cursor: "pointer",
								}}
								alt="logo"
							/>
						</Col>
					</Row>
				</div>
				{isMakroInvoice && (
					<div
						className="well downloadSection"
						style={{ ...props.style, backgroundColor: "white" }}
					>
						<h6 className="summaryinvoicenew">
							Summary Invoice of Makro - {selectedMonth.format("MMMM YYYY")}
						</h6>
						<Row style={{ textAlign: "center" }}>
							{phase1 != null && (
								<Button
									type="submit"
									className={!phase1IsEnabled && "disabled"}
									style={btnStyle}
									onClick={() => {
										if (phase1IsEnabled) {
											setHidden(false);
											setShowGraph("fadeIn");
											fetchSummaryPdfPreview(phase1, "Makro", "");
										} else {
											Noti.create({
												type: "w",
												msg: "Makro Phase 1 sites are still under review",
											});
										}
									}}
								>
									Phase 1
								</Button>
							)}
							{phase2 != null && (
								<Button
									type="submit"
									className={!phase2IsEnabled && "disabled"}
									style={btnStyle}
									onClick={() => {
										if (phase2IsEnabled) {
											setHidden(false);
											setShowGraph("fadeIn");
											fetchSummaryPdfPreview(phase2, "Makro", "2");
										} else {
											Noti.create({
												type: "w",
												msg: "Makro Phase 2 sites are still under review",
											});
										}
									}}
								>
									Phase 2
								</Button>
							)}
						</Row>
					</div>
				)}
				{isCFWInvoice && (
					<div
						className="well downloadSection"
						style={{ ...props.style, backgroundColor: "white" }}
					>
						<h6 className="summaryinvoicenew">
							Summary Invoice of CFW - {selectedMonth.format("MMMM YYYY")}
						</h6>
						<Row style={{ textAlign: "center" }}>
							{phaseGYS6 != null && (
								<Button
									type="submit"
									className={!phaseGYS6IsEnabled && "disabled"}
									style={btnStyle}
									onClick={() => {
										if (phaseGYS6IsEnabled) {
											setHidden(false);
											setShowGraph("fadeIn");
											fetchSummaryPdfPreview(phaseGYS6, "CFW", "GYS6");
										} else {
											Noti.create({
												type: "w",
												msg: "CFW Phase GYS6 sites are still under review",
											});
										}
									}}
								>
									Phase GYS6
								</Button>
							)}
							{phaseS4 != null && (
								<Button
									type="submit"
									className={!phaseS4IsEnabled && "disabled"}
									style={btnStyle}
									onClick={() => {
										if (phaseS4IsEnabled) {
											setHidden(false);
											setShowGraph("fadeIn");
											fetchSummaryPdfPreview(phaseS4, "CFW", "S4");
										} else {
											Noti.create({
												type: "w",
												msg: "CFW Phase S4 sites are still under review",
											});
										}
									}}
								>
									Phase S4
								</Button>
							)}
						</Row>
					</div>
				)}
				{isDoHomeInvoice && (
					<div
						className="well downloadSection"
						style={{ ...props.style, backgroundColor: "white" }}
					>
						<h6 className="summaryinvoicenew">
							Summary Invoice of DoHome - {selectedMonth.format("MMMM YYYY")}
						</h6>
						<Row style={{ textAlign: "center" }}>
							{doHomeEntityGroups.map(({ entityId, sites }) => (
								<Button
									key={entityId}
									type="submit"
									className={!doHomeEnabled[entityId] && "disabled"}
									style={btnStyle}
									onClick={() => {
										if (doHomeEnabled[entityId]) {
											setHidden(false);
											setShowGraph("fadeIn");
											fetchSummaryPdfPreview(sites, "DoHome", entityId);
										} else {
											Noti.create({
												type: "w",
												msg: `DoHome ${entityId} sites are still under review`,
											});
										}
									}}
								>
									{`PHASE ${entityId}`}
								</Button>
							))}
						</Row>
					</div>
				)}
			</div>
			<Noti />
			{!hidden && (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{ textAlign: "center", marginTop: "25px" }}
						className={showGraph}
					>
						{previewLoading ? (
							<LoaderGraph style={{ height: "72vh" }} />
						) : previewPdfUrl ? (
							<>
								<iframe
									title="Summary Invoice Preview"
									style={{ width: "98%", height: "72vh" }}
									src={previewPdfUrl}
								/>
								<Row>
									<Button onClick={downloadPdf}>Download</Button>
								</Row>
							</>
						) : (
							<LoaderGraph style={{ height: "72vh" }} />
						)}
						<Button variant="light" onClick={closePreview} className="closeBtn">
							Close
						</Button>
					</div>
				</div>
			)}
		</>
	) : (
		<LoaderGraph style={{ height: "50vh" }} />
	);
}

export default SummaryInvoice;